import { navigateTo, useRouter } from "@shared/utils.routing";
import { useCallback, useEffect, useState } from "react";

type UsePaginationArgs<T> = {
  itemList: T[];
  itemsToShow?: number;
};

export type PaginationType<T> = {
  prev: () => void;
  next: () => void;
  page: number;
  itemsLength: number;
  items: T;
  totalPages: number;
  pageStart: number;
  pageEnd: number;
};

export const usePagination = <T>({
  itemList,
  itemsToShow = 10
}: UsePaginationArgs<T>): PaginationType<T[]> => {
  const [page, setPage] = useState(1);
  const itemsLength = itemList.length;
  const currentPage = page * itemsToShow - itemsToShow;
  const totalPages = Math.ceil(itemsLength / itemsToShow);
  // The spread is necessary because arrays are frozen in strict mode
  const items = [...itemList].splice(currentPage, itemsToShow);
  const pageStart = (page - 1) * itemsToShow + 1;
  const pageEnd = Math.min(page * itemsToShow, itemsLength);

  const router = useRouter();

  const next = useCallback(() => {
    if (page === totalPages) return;
    setPage(page + 1);
    navigateTo(`${router.asPath}/?page=${page + 1}`);
    window.scrollTo(0, 0);
  }, [page, router.asPath, totalPages]);

  const prev = useCallback(() => {
    if (page === 1) return;
    setPage(page - 1);
    navigateTo(`${router.asPath}/?page=${page - 1}`);
    window.scrollTo(0, 0);
  }, [page, router.asPath]);

  useEffect(() => {
    const queryPage = parseInt(router.query.page as string, 10) || 1;
    if (itemsLength > 0 && totalPages < queryPage)
      navigateTo(`${router.asPath}/?page=${totalPages}`);
    setPage(queryPage);
  }, [router.query.page, itemList, router.asPath, itemsLength, totalPages]);

  return {
    prev,
    next,
    page,
    itemsLength,
    items,
    totalPages,
    pageStart,
    pageEnd
  };
};
